import { DateTime } from 'luxon';

/** Map internal config names to human-readable display labels (mirrors reportConfigurationsConstants). */
const REPORT_NAME_LABELS: Record<string, string> = {
  'total-spending': 'Spending',
  'monthly-spending': 'Monthly Spending',
  income: 'Income',
  'income-expense': 'Income & Expense',
  'net-income': 'Net Income',
  savings: 'Savings',
  'net-worth': 'Net Worth',
  'net-worth-page': 'Net Worth',
  'monthly-summary': 'Monthly Summary',
  'refund-tracker': 'Refund Tracker',
  'credit-score': 'Credit Score',
  taxes: 'Taxes',
  'personal-taxes': 'Personal Taxes',
  custom: 'Custom',
  'profit-loss': 'Profit & Loss',
  'balance-sheet': 'Balance Sheet',
};

const getReportDisplayName = (name: string): string => REPORT_NAME_LABELS[name] || name || '';

export type ReportTrackingConfig = {
  name?: string;
  predefinedConfigId?: string;
  reportType?: string;
  reportSubType?: string;
  persistentType?: string;
  parentPersistentType?: string;
  dateRange?: string;
  timestep?: string;
  chartType?: string;
  comparisonRanges?: ReadonlyArray<{
    dateRange?: string;
    additionalColumnTypes?: readonly string[];
  }>;
};

type ComparisonReportOrigin = 'PREDEFINED' | 'SAVED';
type ComparisonApplyAction = 'ENTERED' | 'UPDATED';

type ResolvedComparisonSpan = {
  startOn?: string;
  endOn?: string;
};

type ReportTrackingOptions = {
  comparisonAction?: ComparisonApplyAction;
  comparisonSpan?: ResolvedComparisonSpan;
  comparisonRangeIndex?: number;
  drillDepth?: number;
};

export type ReportTrackingProps = {
  report_title: string;
  report_type: string | undefined;
  report_subtype: string | undefined;
  predefined_config_id: string | undefined;
  report_origin?: ComparisonReportOrigin;
  action?: ComparisonApplyAction;
  comparison_mode?: string;
  comparison_columns?: readonly string[];
  comparison_duration_days?: number;
  primary_date_range?: string;
  timestep?: string;
  chart_type?: string;
  range?: 'PRIMARY' | 'COMPARISON';
  drill_depth?: number;
};

function getComparisonReportOrigin(config: ReportTrackingConfig): ComparisonReportOrigin {
  const origin = config.parentPersistentType ?? config.persistentType;
  return origin === 'SAVED' || origin === 'CUSTOM' ? 'SAVED' : 'PREDEFINED';
}

function getInclusiveComparisonDurationDays(span: ResolvedComparisonSpan | undefined): number | undefined {
  if (!span?.startOn || !span.endOn) return undefined;

  const start = DateTime.fromISO(span.startOn, { zone: 'utc' }).startOf('day');
  const end = DateTime.fromISO(span.endOn, { zone: 'utc' }).startOf('day');
  if (!start.isValid || !end.isValid || end < start) return undefined;

  return Math.round(end.diff(start, 'days').days) + 1;
}

export function getReportTrackingProps(
  config: ReportTrackingConfig,
  options?: ReportTrackingOptions,
): ReportTrackingProps {
  const comparisonRange = config.comparisonRanges?.[0];
  const comparisonDuration = options?.comparisonAction
    ? getInclusiveComparisonDurationDays(options.comparisonSpan)
    : undefined;
  const hasDrillContext = options?.comparisonRangeIndex !== undefined && options.drillDepth !== undefined;

  return {
    report_title: getReportDisplayName(config.name ?? ''),
    report_type: config.reportType,
    report_subtype: config.reportSubType,
    predefined_config_id: config.predefinedConfigId,
    ...(config.reportType === 'COMPARISON' ? { report_origin: getComparisonReportOrigin(config) } : {}),
    ...(options?.comparisonAction
      ? {
          action: options.comparisonAction,
          comparison_mode: comparisonRange?.dateRange,
          comparison_columns: comparisonRange?.additionalColumnTypes ?? [],
          ...(comparisonDuration === undefined ? {} : { comparison_duration_days: comparisonDuration }),
          primary_date_range: config.dateRange,
          timestep: config.timestep,
          chart_type: config.chartType,
        }
      : {}),
    ...(hasDrillContext
      ? {
          range: options.comparisonRangeIndex === 0 ? ('PRIMARY' as const) : ('COMPARISON' as const),
          comparison_mode: comparisonRange?.dateRange,
          drill_depth: options.drillDepth,
        }
      : {}),
  };
}
